/**
 * Clean Results Modal Wrappers
 * 
 * Replaces the fragmented modal architecture with clean, consistent wrappers
 * that use the UnifiedResultsModal and NavigationService.
 */

import React from 'react';
import PropTypes from 'prop-types';
import UnifiedResultsModal from '../../core/UnifiedResultsModal';
import ModalErrorBoundary from '../../core/ModalErrorBoundary';

/**
 * Evidence Results Modal
 * Shows results for a specific piece of evidence
 */
export const EvidenceResultsModal = ({
  isOpen,
  onClose,
  onRequestEvidenceModal = null, // NEW: Callback to request opening Evidence modal (handled by parent)
  evidenceId,
  evidenceName,
  casefileId,
  casefileName,
  orderId = null,
  jobId = null,
  currentUser = null,
  disablePortal = false, // New prop to control portal behavior
  slideMode = false, // New prop for sliding behavior
  onContextUpdate = null, // New prop for handling context updates when minimized
  disableModalLayerManager = false, // New prop to disable ModalLayerManager when used as child
  disableGlobalNavigation = false // New prop to disable global NavigationService when used as child
}) => {
  // Debug: Track when EvidenceResultsModal mounts
  React.useEffect(() => {
    console.log('🔄 [EvidenceResultsModal] MOUNTED', {
      evidenceId,
      evidenceName,
      isOpen,
      timestamp: Date.now()
    });
    return () => {
      console.log('🔄 [EvidenceResultsModal] UNMOUNTING', {
        evidenceId,
        evidenceName,
        timestamp: Date.now()
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount/unmount
  
  // Internal filter state that can be updated by child components
  const initialFilters = React.useMemo(() => ({
    evidenceIds: evidenceId ? [evidenceId] : []
  }), [evidenceId]);
  
  const [currentFilters, setCurrentFilters] = React.useState(initialFilters);
  const previousIsOpenRef = React.useRef(isOpen);
  const previousEvidenceIdRef = React.useRef(evidenceId);

  // Reset filters when modal opens from closed state OR when evidenceId changes while open
  React.useEffect(() => {
    const wasClosedNowOpen = !previousIsOpenRef.current && isOpen;
    const evidenceChanged = previousEvidenceIdRef.current !== evidenceId && isOpen;
    
    if (wasClosedNowOpen) {
      console.log('🔄 [EvidenceResultsModal] Modal opening from closed - resetting filters', initialFilters);
      setCurrentFilters(initialFilters);
    } else if (evidenceChanged) {
      console.log('🔄 [EvidenceResultsModal] Evidence changed while open - resetting filters', {
        from: previousEvidenceIdRef.current,
        to: evidenceId,
        newFilters: initialFilters
      });
      setCurrentFilters(initialFilters);
    }
    
    previousIsOpenRef.current = isOpen;
    previousEvidenceIdRef.current = evidenceId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, evidenceId]); // Depend on both isOpen and evidenceId

  // Handle filter updates from child components
  const handleUpdateFilters = React.useCallback((newFilters) => {
    console.log('🔄 EvidenceResultsModal: Updating filters:', newFilters);
    setCurrentFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  // Debug: Track when EvidenceResultsModal unmounts
  React.useEffect(() => {
    return () => {
      console.log('🧹 EvidenceResultsModal unmounting:', {
        evidenceId,
        evidenceName,
        isOpen
      });
    };
  }, [evidenceId, evidenceName, isOpen]);

  return (
    <ModalErrorBoundary componentName="Evidence Results Modal">
      <UnifiedResultsModal
        isOpen={isOpen}
        onClose={onClose}
        onRequestEvidenceModal={onRequestEvidenceModal} // Pass callback to parent
        origin="evidence"
        disablePortal={disablePortal}
        slideMode={slideMode}
        onContextUpdate={onContextUpdate}
        disableModalLayerManager={disableModalLayerManager}
        disableGlobalNavigation={disableGlobalNavigation}
        context={{
          evidenceId,
          evidenceName,
          casefileId,
          casefileName,
          orderId,
          jobId,
          currentUser
        }}
        filters={currentFilters}
        onUpdateFilters={handleUpdateFilters}
        ui={{
          showBreadcrumbs: true,
          accordionMode: true,
          pageSize: 20
        }}
      />
    </ModalErrorBoundary>
  );
};

/**
 * Casefile Results Modal
 * Shows results for all evidence in a casefile
 */
export const CasefileResultsModal = ({
  isOpen,
  onClose,
  casefileId,
  casefileName,
  orderId = null,
  jobId = null
}) => {
  // Internal filter state that can be updated by child components
  const initialFilters = React.useMemo(() => ({
    casefileIds: [casefileId]
  }), [casefileId]);
  
  const [currentFilters, setCurrentFilters] = React.useState(initialFilters);
  const previousIsOpenRef = React.useRef(isOpen);

  // Reset filters ONLY when modal opens from closed state (not on remounts)
  React.useEffect(() => {
    const wasClosedNowOpen = !previousIsOpenRef.current && isOpen;
    if (wasClosedNowOpen) {
      setCurrentFilters(initialFilters);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not initialFilters to prevent loops

  // Handle filter updates from child components
  const handleUpdateFilters = React.useCallback((newFilters) => {
    console.log('🔄 CasefileResultsModal: Updating filters:', newFilters);
    setCurrentFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  return (
    <ModalErrorBoundary componentName="Casefile Results Modal">
      <UnifiedResultsModal
        isOpen={isOpen}
        onClose={onClose}
        origin="casefile"
        context={{
          casefileId,
          casefileName,
          orderId,
          jobId
        }}
        filters={currentFilters}
        onUpdateFilters={handleUpdateFilters}
        ui={{
          showBreadcrumbs: true,
          accordionMode: true,
          pageSize: 30
        }}
      />
    </ModalErrorBoundary>
  );
};

/**
 * Global Results Modal
 * Shows all results for the current user across all casefiles
 */
export const GlobalResultsModal = ({
  isOpen,
  onClose,
  userId = null,
  orderId = null,
  jobId = null,
  filters: initialFilters = {}
}) => {
  // Internal filter state that can be updated by child components
  const [currentFilters, setCurrentFilters] = React.useState(initialFilters);
  const previousIsOpenRef = React.useRef(isOpen);

  // Reset filters ONLY when modal opens from closed state (not on remounts)
  React.useEffect(() => {
    const wasClosedNowOpen = !previousIsOpenRef.current && isOpen;
    if (wasClosedNowOpen) {
      setCurrentFilters(initialFilters);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not initialFilters to prevent loops

  // Handle filter updates from child components
  const handleUpdateFilters = React.useCallback((newFilters) => {
    console.log('🔄 [GlobalResultsModal] Updating filters:', newFilters);
    setCurrentFilters(prevFilters => {
      console.log('🔄 [GlobalResultsModal] Previous filters:', prevFilters);
      const updated = {
        ...prevFilters,
        ...newFilters
      };
      console.log('🔄 [GlobalResultsModal] New filters:', updated);
      return updated;
    });
  }, []); // Empty deps - use prevFilters in the setter function

  // Debug logging
  if (process.env.NODE_ENV === 'development' && isOpen) {
    console.log('🌍 GlobalResultsModal opened with:', {
      userId,
      orderId,
      jobId,
      initialFilters,
      currentFilters
    });
  }

  return (
    <ModalErrorBoundary componentName="Global Results Modal">
      <UnifiedResultsModal
        isOpen={isOpen}
        onClose={onClose}
        origin="global"
        context={{
          userId,
          orderId,
          jobId
        }}
        filters={{
          // Global scope - no specific filters, let useOrdersData handle user filtering
          ...currentFilters
        }}
        onUpdateFilters={handleUpdateFilters}
        ui={{
          showBreadcrumbs: true,
          accordionMode: true,
          pageSize: 50
        }}
      />
    </ModalErrorBoundary>
  );
};

/**
 * Single Order Results Modal
 * Shows detailed results for a specific order
 */
export const SingleOrderResultsModal = ({
  isOpen,
  onClose,
  orderId,
  jobId = null,
  casefileId = null,
  casefileName = null,
  evidenceId = null,
  evidenceName = null,
  orderName = null,
  currentUser = null
}) => {
  // Internal filter state that can be updated by child components
  const initialFilters = React.useMemo(() => ({
    orderIds: [orderId]
  }), [orderId]);
  
  const [currentFilters, setCurrentFilters] = React.useState(initialFilters);
  const previousIsOpenRef = React.useRef(isOpen);

  // Reset filters ONLY when modal opens from closed state (not on remounts)
  React.useEffect(() => {
    const wasClosedNowOpen = !previousIsOpenRef.current && isOpen;
    if (wasClosedNowOpen) {
      setCurrentFilters(initialFilters);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not initialFilters to prevent loops

  // Handle filter updates from child components
  const handleUpdateFilters = React.useCallback((newFilters) => {
    console.log('🔄 SingleOrderResultsModal: Updating filters:', newFilters);
    setCurrentFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  return (
    <ModalErrorBoundary componentName="Single Order Results Modal">
      <UnifiedResultsModal
        isOpen={isOpen}
        onClose={onClose}
        origin="single_order"
        context={{
          orderId,
          jobId,
          casefileId,
          casefileName,
          evidenceId,
          evidenceName,
          orderName,
          currentUser
        }}
        filters={currentFilters}
        onUpdateFilters={handleUpdateFilters}
        ui={{
          showBreadcrumbs: true,
          accordionMode: false, // Single order doesn't need accordion
          pageSize: 20
        }}
      />
    </ModalErrorBoundary>
  );
};

/**
 * Bounty Results Modal
 * Shows results filtered by bounty type
 */
export const BountyResultsModal = ({
  isOpen,
  onClose,
  bountyType,
  orderId = null,
  jobId = null,
  additionalFilters = {}
}) => {
  // Internal filter state that can be updated by child components
  const initialFilters = React.useMemo(() => ({
    bountyType,
    ...additionalFilters
  }), [bountyType, additionalFilters]);
  
  const [currentFilters, setCurrentFilters] = React.useState(initialFilters);
  const previousIsOpenRef = React.useRef(isOpen);

  // Reset filters ONLY when modal opens from closed state (not on remounts)
  React.useEffect(() => {
    const wasClosedNowOpen = !previousIsOpenRef.current && isOpen;
    if (wasClosedNowOpen) {
      setCurrentFilters(initialFilters);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not initialFilters to prevent loops

  // Handle filter updates from child components
  const handleUpdateFilters = React.useCallback((newFilters) => {
    console.log('🔄 BountyResultsModal: Updating filters:', newFilters);
    setCurrentFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  return (
    <ModalErrorBoundary componentName="Bounty Results Modal">
      <UnifiedResultsModal
        isOpen={isOpen}
        onClose={onClose}
        origin="bounty"
        context={{
          orderId,
          jobId
        }}
        filters={currentFilters}
        onUpdateFilters={handleUpdateFilters}
        ui={{
          showBreadcrumbs: true,
          accordionMode: true,
          pageSize: 25
        }}
      />
    </ModalErrorBoundary>
  );
};

/**
 * Analysis History Modal
 * Shows historical analysis results
 */
export const AnalysisHistoryModal = ({
  isOpen,
  onClose,
  dateRange = null,
  orderId = null,
  jobId = null
}) => {
  // Internal filter state that can be updated by child components
  const initialFilters = React.useMemo(() => ({
    dateRange,
    statuses: ['completed', 'failed'] // Show finished analyses
  }), [dateRange]);
  
  const [currentFilters, setCurrentFilters] = React.useState(initialFilters);
  const previousIsOpenRef = React.useRef(isOpen);

  // Reset filters ONLY when modal opens from closed state (not on remounts)
  React.useEffect(() => {
    const wasClosedNowOpen = !previousIsOpenRef.current && isOpen;
    if (wasClosedNowOpen) {
      setCurrentFilters(initialFilters);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not initialFilters to prevent loops

  // Handle filter updates from child components
  const handleUpdateFilters = React.useCallback((newFilters) => {
    console.log('🔄 AnalysisHistoryModal: Updating filters:', newFilters);
    setCurrentFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  return (
    <ModalErrorBoundary componentName="Analysis History Modal">
      <UnifiedResultsModal
        isOpen={isOpen}
        onClose={onClose}
        origin="history"
        context={{
          orderId,
          jobId
        }}
        filters={currentFilters}
        onUpdateFilters={handleUpdateFilters}
        ui={{
          showBreadcrumbs: true,
          accordionMode: true,
          pageSize: 40
        }}
      />
    </ModalErrorBoundary>
  );
};

// PropTypes for development-time validation
EvidenceResultsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  evidenceId: PropTypes.string, // Made optional to handle undefined cases gracefully
  evidenceName: PropTypes.string,
  casefileId: PropTypes.string,
  casefileName: PropTypes.string,
  orderId: PropTypes.string,
  jobId: PropTypes.string,
  currentUser: PropTypes.object,
  disablePortal: PropTypes.bool,
  slideMode: PropTypes.bool
};

CasefileResultsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  casefileId: PropTypes.string.isRequired,
  casefileName: PropTypes.string,
  orderId: PropTypes.string,
  jobId: PropTypes.string
};

GlobalResultsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  orderId: PropTypes.string,
  jobId: PropTypes.string,
  filters: PropTypes.object
};

SingleOrderResultsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderId: PropTypes.string.isRequired,
  jobId: PropTypes.string,
  casefileId: PropTypes.string,
  casefileName: PropTypes.string,
  evidenceId: PropTypes.string,
  evidenceName: PropTypes.string,
  orderName: PropTypes.string,
  currentUser: PropTypes.object
};
